import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { GetInspectionItem } from '../../../../../../services/apiTypes';
import CheckBox from '../../Input/CheckBox';
import React, { useEffect, useState } from 'react';
import {
  updateJudgement,
  updateMemo,
} from '../../../../../../stores/inspectionTypeData/slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  JUDGEMENT,
  LEAK_TEST,
} from '../../../../../../_constants/inspectionConst';
import WetLeakTestModal from './WetLeakTestModal';
import { useTranslation } from 'react-i18next';
import {
  getColorByJudgement,
  isNg,
} from '../../../../../../_utils/inspectionUtils';
import { RootState } from '../../../../../../stores/store';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { TEXT_OVERFLOW_2LINES } from '../../../../../../_constants/styleConst';

type LeakTestProps = {
  typeLabel: string;
  itemDataList: GetInspectionItem[];
  beforeItemDataList: GetInspectionItem[];
  isUnfilled: boolean;
  isVisualInspection: boolean;
};

export type SelectItemType = {
  sectionId: string;
  sectionLabel: string;
  detailId: string;
  detailLabel: string;
  memo: string;
};

/** 気密検査の診断画面 */
const LeakTest: React.FC<LeakTestProps> = ({
  typeLabel,
  itemDataList,
  beforeItemDataList,
  isUnfilled,
  isVisualInspection,
}) => {
  const theme = useTheme();
  const style = {
    table: {
      fontSize: theme.typography.caption.fontSize,
    },
  };
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  /** 判定基準のテキストがある項目を含むか */
  const [hasCriteriaInType, setHasCriteriaInType] = useState(false);
  /** モーダルの開閉フラグ */
  const [isOpenModal, setIsOpenModal] = useState(false);
  /** モーダルで選択済みの問題箇所のリスト */
  const [selectedItems, setSelectedItems] = useState<SelectItemType[]>([]);

  const wetLeakTestItems = itemDataList.find(
    (item) => item.id === LEAK_TEST.wetLeakTestItemId,
  );

  useEffect(() => {
    // 表示切替用フラグの初期化
    setHasCriteriaInType(
      itemDataList.some(({ criteria }) => criteria.text !== ''),
    );
    // 表示する気密不良箇所のデータ初期化
    if (!wetLeakTestItems || !isNg(wetLeakTestItems.judgement)) {
      setSelectedItems([]);
      return;
    }

    const items: SelectItemType[] = [];
    wetLeakTestItems.itemData.forEach((problem) => {
      if (isNg(problem.judgement)) {
        problem.itemData.forEach((detail) => {
          if (isNg(detail.judgement)) {
            items.push({
              sectionId: problem.id,
              sectionLabel: problem.label,
              detailId: detail.id,
              detailLabel: detail.label,
              memo: detail.memo,
            });
          }
        });
      }
    });

    setSelectedItems(items);
  }, [wetLeakTestItems]);

  const handleOpen = () => {
    setIsOpenModal(true);
  };

  const handleClose = (
    shouldUpdate: boolean,
    selectedItemsModal: SelectItemType[],
  ) => {
    if (shouldUpdate) {
      setSelectedItems(selectedItemsModal);
      if (wetLeakTestItems && wetLeakTestItems.itemData) {
        wetLeakTestItems.itemData.forEach((item) => {
          const isSelected = selectedItemsModal
            .map((selectedItem) => selectedItem.sectionId)
            .includes(item.id);
          if (isSelected) {
            dispatch(
              updateJudgement({
                categoryId: LEAK_TEST.categoryId,
                typeId: LEAK_TEST.typeId,
                path: [LEAK_TEST.wetLeakTestItemId, item.id],
                judgement: JUDGEMENT.NG,
              }),
            );
            item.itemData.forEach((detail) => {
              const selectedDetail = selectedItemsModal.find(
                (selectedDetailItem) =>
                  selectedDetailItem.detailId === detail.id,
              );
              // 該当するdetailIdがある場合はNG、そうでなければOK
              const detailJudgement = selectedDetail
                ? JUDGEMENT.NG
                : JUDGEMENT.OK;
              const detailMemo = selectedDetail ? selectedDetail.memo : '';
              dispatch(
                updateJudgement({
                  categoryId: LEAK_TEST.categoryId,
                  typeId: LEAK_TEST.typeId,
                  path: [LEAK_TEST.wetLeakTestItemId, item.id, detail.id],
                  judgement: detailJudgement,
                }),
              );
              dispatch(
                updateMemo({
                  categoryId: LEAK_TEST.categoryId,
                  typeId: LEAK_TEST.typeId,
                  path: [LEAK_TEST.wetLeakTestItemId, item.id, detail.id],
                  memo: detailMemo,
                }),
              );
            });
          } else {
            dispatch(
              updateJudgement({
                categoryId: LEAK_TEST.categoryId,
                typeId: LEAK_TEST.typeId,
                path: [LEAK_TEST.wetLeakTestItemId, item.id],
                judgement: JUDGEMENT.OK,
              }),
            );
            item.itemData.forEach((detail) => {
              dispatch(
                updateJudgement({
                  categoryId: LEAK_TEST.categoryId,
                  typeId: LEAK_TEST.typeId,
                  path: [LEAK_TEST.wetLeakTestItemId, item.id, detail.id],
                  judgement: JUDGEMENT.OK,
                }),
              );
            });
          }
        });
      }
    } else {
      // キャンセルされた場合
      if (wetLeakTestItems && wetLeakTestItems.itemData) {
        dispatch(
          updateJudgement({
            categoryId: LEAK_TEST.categoryId,
            typeId: LEAK_TEST.typeId,
            path: [LEAK_TEST.wetLeakTestItemId],
            judgement: JUDGEMENT.EMPTY,
          }),
        );
        wetLeakTestItems.itemData.forEach((item) => {
          dispatch(
            updateJudgement({
              categoryId: LEAK_TEST.categoryId,
              typeId: LEAK_TEST.typeId,
              path: [LEAK_TEST.wetLeakTestItemId, item.id],
              judgement: JUDGEMENT.EMPTY,
            }),
          );
          item.itemData.forEach((detail) => {
            dispatch(
              updateJudgement({
                categoryId: LEAK_TEST.categoryId,
                typeId: LEAK_TEST.typeId,
                path: [LEAK_TEST.wetLeakTestItemId, item.id, detail.id],
                judgement: JUDGEMENT.EMPTY,
              }),
            );
          });
        });
      }
    }
    setIsOpenModal(false);
  };

  /** 親の検査項目のチェックボックスをOK・N/Aにした時 */
  const clearProblemCheck = (itemId: string, judgement: number) => {
    const problemData = itemDataList.find((item) => item.id === itemId);
    if (!problemData) {
      return;
    }
    const problemDataList = problemData.itemData;
    if (!problemDataList) {
      return;
    }
    problemDataList.forEach((problem) => {
      dispatch(
        updateJudgement({
          categoryId: LEAK_TEST.categoryId,
          typeId: LEAK_TEST.typeId,
          path: [itemId, problem.id],
          judgement,
        }),
      );
      if (problem.itemData) {
        problem.itemData.forEach((detail) => {
          dispatch(
            updateJudgement({
              categoryId: LEAK_TEST.categoryId,
              typeId: LEAK_TEST.typeId,
              path: [itemId, problem.id, detail.id],
              judgement,
            }),
          );
          dispatch(
            updateMemo({
              categoryId: LEAK_TEST.categoryId,
              typeId: LEAK_TEST.typeId,
              path: [itemId, problem.id, detail.id],
              memo: '',
            }),
          );
        });
      }
    });
  };

  return (
    <Box>
      {/* ラベルの行 */}
      <Grid container>
        <Grid size={6} mb={1} sx={{ display: 'flex' }}>
          {isVisualInspection && (
            <Box sx={{ display: 'inline', mx: 1.5 }}>
              <VisibilityIcon color="primary" />
            </Box>
          )}
          <Typography
            sx={[
              { fontSize: theme.typography.body1.fontSize },
              TEXT_OVERFLOW_2LINES,
            ]}
          >
            {typeLabel}
          </Typography>
        </Grid>
        <Grid size={1}>{/* 判定結果 */}</Grid>
        <Grid
          size={3}
          sx={{ fontSize: theme.typography.caption.fontSize }}
          textAlign="center"
          alignSelf="end"
        >
          {hasCriteriaInType ? t('LBL_CRITERIA') : ''}
        </Grid>
        <Grid size={2}></Grid>
      </Grid>

      {itemDataList.map((item, i) => {
        const lineStyle = {
          height: '40px',
          alignItems: 'center',
          bgcolor: i % 2 === 0 ? theme.palette.background.paper : '',
        };
        return (
          <Grid container key={`leak_test_${i}`} sx={lineStyle}>
            <Grid size={0.5}>{/* (空) */}</Grid>
            <Grid
              size={5.5}
              sx={{
                ...TEXT_OVERFLOW_2LINES,
                fontSize: theme.typography.caption.fontSize ?? '0.75rem',
              }}
            >
              {item.label}
            </Grid>
            <Grid size={1} textAlign="center">
              <CheckBox
                itemId={`leak_test_${i}`}
                categoryId={LEAK_TEST.categoryId}
                typeId={LEAK_TEST.typeId}
                judgement={item.judgement}
                path={[item.id]}
                onOk={() => clearProblemCheck(item.id, JUDGEMENT.OK)}
                onNg={
                  item.id === LEAK_TEST.wetLeakTestItemId
                    ? () => handleOpen()
                    : () => {}
                }
                onNa={() => clearProblemCheck(item.id, JUDGEMENT.NA)}
                borderColor={getColorByJudgement(
                  beforeItemDataList.find((before) => before.id === item.id)
                    ?.judgement,
                )}
                isUnfilled={isUnfilled}
              />
            </Grid>
            <Grid size={3} textAlign="center">
              <Typography
                sx={{
                  fontSize: theme.typography.caption.fontSize,
                }}
              >
                {item.criteria.text}
              </Typography>
            </Grid>
            <Grid size={2}></Grid>
          </Grid>
        );
      })}

      {/* 気密不良箇所のテーブル */}
      {selectedItems.length > 0 && (
        <Box pt={3}>
          <TableContainer
            component={Paper}
            sx={{
              bgcolor: 'background.default',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell sx={style.table}>
                    {t('LBL_LEAKAGE_POINT')}
                  </TableCell>
                  <TableCell sx={style.table}>{t('LBL_MEMO')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedItems.map((item, index) => (
                  <TableRow
                    key={`leak_test_table_${index}`}
                    sx={{ height: '12px' }}
                  >
                    <TableCell sx={style.table}>{item.sectionLabel}</TableCell>
                    <TableCell>
                      <Typography sx={[style.table, TEXT_OVERFLOW_2LINES]}>
                        {item.detailLabel}
                      </Typography>
                    </TableCell>
                    <TableCell sx={style.table}>{item.memo}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {wetLeakTestItems && wetLeakTestItems.itemData.length > 0 && (
        <WetLeakTestModal
          isOpen={isOpenModal}
          wetLeakTestItems={wetLeakTestItems}
          handleClose={handleClose}
          repairRequest={repairRequest}
        />
      )}
    </Box>
  );
};

export default LeakTest;
